import React from "react";
import ActionsComponent from "../../ActivationCode/tableComponents/actionsComponent";
import {ADMIN, DOCTOR, ROLE_LABELS, ROLE_ORDER, SUPER_ADMIN} from "../../../constants/roles";

const SignatureImg = ({value}) => <img src={value} alt="sign" className="table-signature"/>

export const cliniciansTableHeader = [
    {label: "First name", sortOption: false, classname: "font-size-13 table-caption-label"},
    {label: "Last name", sortOption: false, classname: "font-size-13 table-caption-label disable-word-wrap"},
    {label: "Email address", sortOption: false, classname: "font-size-13 table-caption-label disable-word-wrap"},
    {label: "GMC", sortOption: false, classname: "font-size-13 table-caption-label disable-word-wrap"},
    {label: "Signature", sortOption: false, classname: "font-size-13 table-caption-label disable-word-wrap"},
    {
        label: "Action",
        sortOption: false,
        classname: "font-size-13 table-caption-label disable-word-wrap action-table-cell"
    },
];

export const cliniciansTableStructure = [
    {
        name: "firstName",
        classname: "font-size-14 pt-20px pb-20px border-bottom fullname-table-cell",
        keyProp: "firstName"
    },
    {name: "lastName", classname: "font-size-13 pt-20px pb-20px border-bottom", keyProp: "lastName"},
    {name: "email", classname: "font-size-13 pt-20px pb-20px border-bottom", keyProp: "email"},
    {name: "gmc", classname: "font-size-13 pt-20px pb-20px border-bottom", keyProp: "doctor.gmc"},
    {
        name: "signature",
        classname: "font-size-13 pt-20px pb-20px border-bottom",
        keyProp: "doctor.link",
        Component: SignatureImg
    }, //todo: img component
    // { name: "actions", classname: "font-size-13 pt-20px pb-20px border-bottom", keyProp: "id", Component: (({value}) => value) },
]

/**
 * Super admins and Admins: whether this Doctor is exempt from "Own patients only" - sees every doctor's
 * patients in the default lists while that switch is on. For Doctors who work across every doctor's
 * list (ID checks, cover). Admins and super admins always see every list (the server exempts them by
 * role), so there is nothing to grant.
 *
 * The box's words never change with the box: "Always sees all patients" is what TICKING means. Words
 * that followed the state ("Own patients" unticked, "All patients" ticked) read as the opposite.
 */
export const FullPatientAccessToggle = ({ clinician, onToggle }) => {
    if (clinician.role === SUPER_ADMIN) {
        return <span className="text-muted" data-testid={`full-access-${clinician.id}`}>All patients (super admin)</span>;
    }
    if (clinician.role === ADMIN) {
        return <span className="text-muted" data-testid={`full-access-${clinician.id}`}>All patients (admin)</span>;
    }

    return (
        <label className="d-flex align-items-center mb-0 cursor-pointer">
            <input
                type="checkbox"
                className="mr-2"
                data-testid={`full-access-${clinician.id}`}
                checked={Boolean(clinician.hasFullPatientAccess)}
                onChange={event => onToggle(clinician, event.target.checked)}
            />
            <span>Always sees all patients</span>
        </label>
    );
};

/**
 * superAdmin only: the clinician's role, and the way to change it. Choosing a role only ASKS - the
 * page confirms before anything changes, because a change signs the person out everywhere.
 * "Sign out everywhere" is its own action (onRequestSignOut) and never sends a role.
 */
export const RoleCell = ({ clinician, currentUserId, onRequestRoleChange, onRequestSignOut, onShowHistory }) => {
    const isSelf = clinician.id === currentUserId;
    const changeable = ROLE_ORDER.includes(clinician.role);

    if (!changeable) {
        return <span>{ROLE_LABELS[clinician.role] || "-"}</span>;
    }

    return (
        <div>
            <select
                className="form-control form-control-sm"
                aria-label={`Role of ${clinician.firstName} ${clinician.lastName}`}
                data-testid={`role-${clinician.id}`}
                value={String(clinician.role)}
                disabled={isSelf}
                onChange={event => onRequestRoleChange(clinician, Number(event.target.value))}
            >
                {ROLE_ORDER.map(role => (
                    <option key={role} value={String(role)}>{ROLE_LABELS[role]}</option>
                ))}
            </select>
            {isSelf ? (
                <div className="text-muted font-size-12 mt-1">You can&apos;t change your own role</div>
            ) : (
                <div className="font-size-12 mt-1 disable-word-wrap">
                    <button
                        type="button"
                        className="btn btn-link p-0 font-size-12"
                        data-testid={`sign-out-${clinician.id}`}
                        onClick={() => onRequestSignOut(clinician)}
                    >
                        Sign out everywhere
                    </button>
                    <span className="text-muted mx-1">·</span>
                    <button
                        type="button"
                        className="btn btn-link p-0 font-size-12"
                        data-testid={`role-history-${clinician.id}`}
                        onClick={() => onShowHistory(clinician)}
                    >
                        History
                    </button>
                </div>
            )}
        </div>
    );
};

export const EXEMPT_COLUMN_HEADING = "Exempt from 'Own patients only'";

/**
 * Whether Edit is offered on this row. A super admin may edit anyone, themselves and deleted accounts
 * included. Anyone else (an Admin - Doctors never open this page) only a LIVE Doctor's account, never
 * their own: the server refuses an Admin anything else (ADMINS_MANAGE_DOCTORS_ONLY), because an email
 * change plus a password reset would hand over the account. A deleted account comes with isDeleted: true.
 */
export const canEditClinician = (clinician, { isSuperAdmin, currentUserId }) => (
    isSuperAdmin || (clinician.role === DOCTOR && clinician.id !== currentUserId && !clinician.isDeleted)
);

/**
 * An Admin is not offered "Always sees all patients" on a deleted Doctor's row: the server refuses it
 * (CLINICIAN_NOT_FOUND). Super admins: as before.
 */
const hidesFullAccess = (clinician, isSuperAdmin) => (
    !isSuperAdmin && clinician.role === DOCTOR && Boolean(clinician.isDeleted)
);

const headerCell = label => ({ label, sortOption: false, classname: "font-size-13 table-caption-label disable-word-wrap" });
const bodyCell = "font-size-13 pt-20px pb-20px border-bottom";

/**
 * The table's header and body, built together so a column can never land under the wrong heading.
 * Pass `superAdmin` (only for a signed-in super admin) to add the Role column (Change role, Sign out
 * everywhere, History), and `onToggleFullAccess` (super admins and Admins) to add the "Exempt from
 * 'Own patients only'" column.
 *
 * @param {object} options
 * @param {function} options.handleEdit
 * @param {number} [options.currentUserId]
 * @param {object} [options.superAdmin]  { onRequestRoleChange, onRequestSignOut, onShowHistory }
 * @param {function} [options.onToggleFullAccess]
 * @returns {{header: object[], structure: object[]}}
 */
export const cliniciansColumns = ({ handleEdit, currentUserId, superAdmin, onToggleFullAccess }) => {
    const header = cliniciansTableHeader.slice(0, -1);
    const structure = cliniciansTableStructure.slice();

    if (superAdmin) {
        header.push(headerCell("Role"));
        structure.push(
            {
                name: "role",
                classname: bodyCell,
                Component: (({ value }) => (
                    <RoleCell
                        clinician={value}
                        currentUserId={currentUserId}
                        onRequestRoleChange={superAdmin.onRequestRoleChange}
                        onRequestSignOut={superAdmin.onRequestSignOut}
                        onShowHistory={superAdmin.onShowHistory}
                    />
                )),
            },
        );
    }

    if (onToggleFullAccess) {
        header.push(headerCell(EXEMPT_COLUMN_HEADING));
        structure.push({
            name: "fullPatientAccess",
            classname: bodyCell,
            Component: (({ value }) => (hidesFullAccess(value, Boolean(superAdmin))
                ? <span className="text-muted" data-testid={`full-access-${value.id}-deleted`}>Deleted account</span>
                : <FullPatientAccessToggle clinician={value} onToggle={onToggleFullAccess}/>)),
        });
    }

    header.push(cliniciansTableHeader[cliniciansTableHeader.length - 1]);
    structure.push({
        name: "actions",
        classname: "font-size-13 pt-20px pb-20px pr-2 pl-3 border-bottom",
        Component: (({ value }) => (
            <ActionsComponent
                item={value}
                handleEdit={canEditClinician(value, { isSuperAdmin: Boolean(superAdmin), currentUserId }) ? handleEdit : null}
            />
        )),
    });

    return { header, structure };
};
